import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type WheelEvent,
} from "react";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DesktopDateTimePicker,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";
import { md5 } from "js-md5";
import { useTranslation } from "react-i18next";
import { base64 } from "rfc4648";
import { match } from "ts-pattern";

import { dayjs, getNameInitials, UK_TIME_ZONE } from "@thalamos/common";
import type { Dayjs } from "@thalamos/common";
import type { UploadedFile } from "@thalamos/frontend";
import { Base64FileUpload, base64PayloadOf } from "@thalamos/frontend";
import type {
  CreateSection12PaymentRequestInput,
  CreateSection12PaymentRequestItemInput,
  ExtendedThalamosUser,
  FormContextData,
  GuestSessionAccreditation,
  LimitedUpdateSection12PaymentRequestInput,
  LocalAuthorityPaymentRate,
  Section12PaymentRequestOutcomeType,
  Section12PaymentRequestSummary,
  Section12PaymentRequestWithDetails,
  Section12TeamInvoicing,
  UpdateSection12PaymentRequestInput,
  UserSearchResult,
} from "@thalamos/aspire-common";
import {
  canDoctorInviteGuestS12Amhp,
  ESTIMATED_RATE_KEY,
  ESTIMATED_RATE_LABEL,
  hasAspireReconciliationFlag,
  isDoctorAccreditation,
  isGuestUserSession,
  isValidEmail,
  redoStatusLabel,
} from "@thalamos/aspire-common";

import {
  noMedRecTarget,
  workItemRecordsNoMedRec,
} from "../helpers/formContextLogic.js";
import { isGuestDoctorForFormContext } from "../helpers/workItemHelpers.js";
import { extractRejectionReason } from "./AmhpPaymentRequestReviewPopup.js";
import { GuestAccreditationGateDialog } from "./GuestAccreditationGateDialog.js";
import { NO_MED_REC_REASON_OPTIONS } from "./noMedRecReasonOptions.js";
import { RecordReasonDialog } from "./RecordReasonDialog.js";
import type { SelectedLocalAuthority } from "./S12DoctorRequest/components/LocalAuthorityPicker.js";
import { LocalAuthorityPicker } from "./S12DoctorRequest/components/LocalAuthorityPicker.js";
import type {
  PriorRequestLink,
  SignedMedRecOption,
} from "./signedMedRecOptions.js";
import {
  findPriorRequestLink,
  findRedoReplacementOption,
  signedMedRecOptions,
} from "./signedMedRecOptions.js";
import { api, apiHooks } from "~/api.js";
import {
  AddressField,
  Banner,
  BannerList,
  renderErrorToast,
  renderSuccessToast,
  Typeahead,
} from "~/components/design-system/index.js";
import { ukInstantOfWallClock } from "~/components/form/ukInstantOfWallClock.js";
import { config } from "~/config.js";
import { ManagedLocationsContext } from "~/Contexts.js";
import { assertOkResponse } from "~/helpers/assertOkResponse.js";
import { logActionToDatadog } from "~/tracing.js";

/**
 * Deliberately free of any suggestion to retry: the refusal is a property of
 * the team context, so repeating the request can only fail again.
 */
export const NO_MED_REC_WRONG_TEAM_MESSAGE =
  "This assessment is not held by the team you are currently working in, so the reason cannot be recorded here. Switch to the team it was sent to, then record it there.";

/**
 * The counterpart refusal, for when the team does hold the assessment. Naming
 * the work is the whole point: telling this reader to switch team would send
 * them somewhere that does not exist.
 */
export const NO_MED_REC_OTHER_WORKFLOW_MESSAGE =
  "Your team's work on this assessment is not a medical recommendation, so this outcome cannot be recorded against it.";

interface AmhpTeamOption {
  id: string;
  name: string;
  section12PaymentsEnabled: boolean;
  section12PrecisePaymentsEnabled: boolean;
  /**
   * The authority a request to this team is billed to. Null prices by the
   * patient's own authority; absent means the answer is not yet known.
   */
  section12Invoicing?: Section12TeamInvoicing | null;
}

type AmhpSearchResultContext = UserSearchResult["contexts"][number];

function filterAmhpContextsByTrainingForm(
  contexts: AmhpSearchResultContext[],
  isTrainingFormContext: boolean,
): AmhpSearchResultContext[] {
  return contexts.filter(
    (c) =>
      c.teamType === "amhp" &&
      // Treat missing training flags as "real" to preserve backwards
      // compatibility with older payloads.
      (c.isTrainingOrganisation ?? false) === isTrainingFormContext,
  );
}

export function filterAmhpSearchResultsByTrainingForm(
  results: UserSearchResult[],
  isTrainingFormContext: boolean,
): UserSearchResult[] {
  return results
    .map((result) => ({
      ...result,
      contexts: filterAmhpContextsByTrainingForm(
        result.contexts,
        isTrainingFormContext,
      ),
    }))
    .filter((result) => result.contexts.length > 0);
}

const RECEIPT_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const RECEIPT_VALID_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
];
const RECEIPT_ACCEPT = RECEIPT_VALID_MIME_TYPES.join(",");
const AMHP_TEAM_SECTION12_PAYMENTS_DISABLED_MESSAGE =
  "This AMHP team does not have Dr Finder Payments enabled. Please speak to the AMHP team responsible to upgrade their eMHA service.";
const ASSESSMENT_DATE_TIME_FUTURE_ERROR_MESSAGE =
  "You cannot enter a date and/or time in the future.";
const ASSESSMENT_DATE_TIME_HELPER_TEXT = "UK date and time (dd/mm/yyyy hh:mm)";
const ASSESSMENT_DATE_TIME_FUTURE_ERROR_CODES = new Set([
  "disableFuture",
  "maxDate",
  "maxTime",
]);
// Earliest assessment date a payment request may be raised for: the date the
// Dr Finder payments service went live. No claimable assessment predates it, so
// the floor is an absolute date rather than a window relative to today.
export const ASSESSMENT_DATE_TIME_EARLIEST_DATE = "2026-04-29";
const earliestAssessmentDate = dayjs.tz(
  ASSESSMENT_DATE_TIME_EARLIEST_DATE,
  UK_TIME_ZONE,
);
export const ASSESSMENT_DATE_TIME_PAST_ERROR_MESSAGE = `You cannot enter a date and/or time before ${earliestAssessmentDate.format(
  "D MMMM YYYY",
)}.`;
// "minTime" is included only for defensive symmetry with the future set's
// "maxTime"; no minTime prop is set, so MUI only actually emits "minDate".
const ASSESSMENT_DATE_TIME_PAST_ERROR_CODES = new Set(["minDate", "minTime"]);

// Banner copy for the no-LA-found case, dependent on the reconciliation flag.
// Reconciliation-on: the doctor can self-serve by clicking "Unknown" in the
// patient banner to add the NHS number.
// Reconciliation-off: the doctor must ask the approving AMHP to do it instead.
export const NO_LA_BANNER_RECONCILIATION_ON =
  'No responsible local authority could be determined automatically. This could delay payment. If the patient\'s NHS number is currently recorded as "Unknown" but you know it, please return to the patient banner, click "Unknown", and add the patient\'s NHS number.';
export const NO_LA_BANNER_RECONCILIATION_OFF =
  'No responsible local authority could be determined automatically. This could delay payment. If the patient\'s NHS number is known, please ask the AMHP approving the payment request to add it by clicking "Unknown" in the patient banner and adding the NHS number.';
type UploadedReceiptFile = UploadedFile & { uploadedKey: string };

function blurNumberInputOnWheel(event: WheelEvent<HTMLInputElement>) {
  event.currentTarget.blur();
}

/** Hints to pre-populate the dialog (e.g., from a work item context) */
export interface S12PaymentRequestDialogHints {
  /** Pre-select outcome type */
  initialOutcomeType?: Section12PaymentRequestOutcomeType;
  /** Pre-select form ID (for 'form' outcome type) */
  initialFormId?: string;
}

/** API functions that can be injected for testing */
export interface S12PaymentRequestDialogApi {
  createPaymentRequest: (
    input: CreateSection12PaymentRequestInput,
  ) => Promise<void>;
  updatePaymentRequest: (
    paymentRequestId: string,
    input: UpdateSection12PaymentRequestInput,
  ) => Promise<void>;
  limitedUpdatePaymentRequest: (
    paymentRequestId: string,
    input: LimitedUpdateSection12PaymentRequestInput,
  ) => Promise<void>;
  /**
   * Resolves with the response status rather than throwing on it: a refusal is
   * an outcome the dialog has to explain, not an exception. Rejects only when
   * no response arrives or the server fails outright.
   */
  recordReasonForNotProceedingMedRec: (
    workItemId: string,
    reason: string,
    reasonDescription?: string,
  ) => Promise<{ status: number }>;
  /**
   * The authority a request to this team would be billed to, or null where the
   * server gave no answer.
   */
  getTeamInvoicing: (
    formContextId: string,
    amhpTeamId: string,
  ) => Promise<Section12TeamInvoicing | null>;
}

/**
 * Throws for any status outside 2xx, with the body's reason (as
 * `extractRejectionReason` reads it) as the message when it has one, so a
 * failed payment request write reaches the caller's failure path whether or
 * not the API client rejects it.
 */
export function assertPaymentRequestWriteAccepted(response: {
  status: number;
  data: unknown;
}): void {
  if (response.status < 200 || response.status >= 300) {
    throw new Error(
      extractRejectionReason(response.data) ??
        `Payment request write failed with status ${String(response.status)}`,
    );
  }
}

/** Default API implementation using the real API */
const defaultApi: S12PaymentRequestDialogApi = {
  createPaymentRequest: async (input) => {
    assertPaymentRequestWriteAccepted(
      await api.section12.createPaymentRequest(input),
    );
  },
  updatePaymentRequest: async (paymentRequestId, input) => {
    assertPaymentRequestWriteAccepted(
      await api.section12.updatePaymentRequest(paymentRequestId, input),
    );
  },
  limitedUpdatePaymentRequest: async (paymentRequestId, input) => {
    assertPaymentRequestWriteAccepted(
      await api.section12.limitedUpdatePaymentRequest(paymentRequestId, input),
    );
  },
  recordReasonForNotProceedingMedRec: async (
    workItemId,
    reason,
    reasonDescription,
  ) => {
    const response = await api.admissions.recordReasonForNotProceedingMedRec(
      workItemId,
      reason,
      reasonDescription,
    );

    return { status: response.status };
  },
  getTeamInvoicing: async (formContextId, amhpTeamId) => {
    try {
      const response = await api.section12.getTeamInvoicing(
        formContextId,
        amhpTeamId,
      );
      return response.status === 200 ? response.data : null;
    } catch {
      return null;
    }
  },
};

interface S12PaymentRequestDialogProps {
  formContext: FormContextData;
  user: ExtendedThalamosUser;
  onClose: () => void;
  reloadFormContext: () => void;
  /** Optional hints to pre-populate dialog fields */
  hints?: S12PaymentRequestDialogHints;
  /** Optional API override for testing */
  apiOverride?: Partial<S12PaymentRequestDialogApi>;
  editingRequest?: Section12PaymentRequestWithDetails;
}

export function S12PaymentRequestDialog({
  formContext,
  user,
  onClose,
  reloadFormContext,
  hints,
  apiOverride,
  editingRequest,
}: S12PaymentRequestDialogProps) {
  const isEditMode = !!editingRequest;
  // "limited" edit modes: server-side, only items may change while the
  // request sits in value-incorrect / receipt-missing. The status is
  // preserved by the limited-edit endpoint.
  const limitedScope: "value-incorrect" | "receipt-missing" | null =
    editingRequest?.status === "value-incorrect" ||
    editingRequest?.status === "receipt-missing"
      ? editingRequest.status
      : null;
  const isLimitedEdit = limitedScope !== null;
  const lockAmountEdits = limitedScope === "receipt-missing";
  const apiClient: S12PaymentRequestDialogApi = {
    ...defaultApi,
    ...apiOverride,
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const localAuthoritySelectionLabel = t(
    "pages.s12PaymentRequestDialog.localAuthoritySelectionLabel",
  );
  const arrangingLocalAuthorityLabel = t(
    "pages.s12PaymentRequestDialog.arrangingLocalAuthorityLabel",
  );

  // Access managed locations to get LA rates
  const managedLocationsContext = useContext(ManagedLocationsContext);

  // Extract AMHP teams with accepted/completed work items
  const amhpTeams = formContext.workItems
    .filter(
      (wi) =>
        wi.team.type === "amhp" &&
        (wi.status === "accepted" || wi.status === "completed"),
    )
    .reduce<AmhpTeamOption[]>((acc, wi) => {
      if (!acc.some((t) => t.id === wi.teamId)) {
        acc.push({
          id: wi.teamId,
          name: wi.team.name,
          section12PaymentsEnabled: wi.team.section12PaymentsEnabled === true,
          section12PrecisePaymentsEnabled:
            wi.team.section12PrecisePaymentsEnabled === true,
          ...(wi.team.section12Invoicing && {
            section12Invoicing: {
              localAuthorityId: wi.team.section12Invoicing.localAuthorityId,
              localAuthorityName: wi.team.section12Invoicing.localAuthorityName,
              icbName: wi.team.section12Invoicing.icbName,
              scenario: wi.team.section12Invoicing.scenario,
            },
          }),
        });
      }
      return acc;
    }, []);

  const noMedRec = useMemo(() => {
    const target = noMedRecTarget(formContext, user);

    return {
      workItem: target.workItem,
      refusal: match(target.refusal)
        .with("wrong-team", () => NO_MED_REC_WRONG_TEAM_MESSAGE)
        .with("other-workflow", () => NO_MED_REC_OTHER_WORKFLOW_MESSAGE)
        .with(undefined, () => undefined)
        .exhaustive(),
    };
  }, [formContext, user]);

  const noMedRecWorkItem = noMedRec.workItem;
  const isNoMedRecFlow = workItemRecordsNoMedRec(noMedRecWorkItem);

  // A reason already on the work item, but only while the decision it records
  // still stands: unarchiving leaves the reason in place, so reading it without
  // the lifecycle gate would carry a withdrawn decision onto a new request.
  const recordedNoMedRecReason = isNoMedRecFlow
    ? noMedRecWorkItem?.reason
    : undefined;

  // Recording the decision writes the reason onto `noMedRecWorkItem` and
  // completes it, so the outcome needs one. Editing never touches the work item
  // — the reason travels on the payment request itself — so it stays available
  // there.
  const canRecordNoMedRec = isEditMode || noMedRecWorkItem !== undefined;

  const doctorSignedForms = useMemo(
    () =>
      signedMedRecOptions({
        forms: formContext.forms,
        userId: user.id,
        linkedFormId: editingRequest?.formId,
      }),
    [formContext.forms, user.id, editingRequest?.formId],
  );

  const onlyDoctorSignedForm =
    doctorSignedForms.length === 1 ? doctorSignedForms[0] : undefined;
  const onlyAmhpTeam = amhpTeams.length === 1 ? amhpTeams[0] : undefined;

  // Check for existing payment requests for each form. In edit mode, exclude
  // the request currently being edited so the doctor can keep the form
  // already linked to it.
  const existingPaymentRequestFormIds = useMemo(() => {
    const requests = formContext.section12PaymentRequests ?? [];
    return new Set(
      requests
        .filter(
          (pr: Section12PaymentRequestSummary) =>
            pr.doctorUserId === user.id &&
            pr.formId &&
            pr.id !== editingRequest?.id,
        )
        .map((pr: Section12PaymentRequestSummary) => pr.formId as string),
    );
  }, [formContext.section12PaymentRequests, user.id, editingRequest?.id]);

  const priorRequestLinkByFormId = useMemo(() => {
    const byFormId = new Map<string, PriorRequestLink>();
    for (const option of doctorSignedForms) {
      const prior = findPriorRequestLink({
        formId: option.formId,
        forms: formContext.forms,
        requestedFormIds: existingPaymentRequestFormIds,
      });
      if (prior !== undefined) byFormId.set(option.formId, prior);
    }
    return byFormId;
  }, [doctorSignedForms, formContext.forms, existingPaymentRequestFormIds]);

  // The replacement to point the doctor to when the edited request is linked to
  // a voided form; the link itself is kept until they select it.
  const linkedFormReplacement = useMemo(() => {
    const linkedFormId = editingRequest?.formId;
    if (!linkedFormId) return undefined;
    const linkedOption = doctorSignedForms.find(
      (option) => option.formId === linkedFormId,
    );
    if (linkedOption?.isVoid !== true) return undefined;
    return findRedoReplacementOption({
      formId: linkedFormId,
      options: doctorSignedForms,
      forms: formContext.forms,
    });
  }, [editingRequest?.formId, doctorSignedForms, formContext.forms]);

  // Check if there's already a payment request without a form ID (for no med rec flow)
  const hasExistingNoMedRecPaymentRequest = useMemo(() => {
    const requests = formContext.section12PaymentRequests ?? [];
    return requests.some(
      (pr: Section12PaymentRequestSummary) =>
        pr.doctorUserId === user.id && !pr.formId,
    );
  }, [formContext.section12PaymentRequests, user.id]);

  // Check if doctor has any existing payment requests for this assessment
  const existingDoctorRequests = useMemo(() => {
    const requests = formContext.section12PaymentRequests ?? [];
    return requests.filter(
      (pr: Section12PaymentRequestSummary) => pr.doctorUserId === user.id,
    );
  }, [formContext.section12PaymentRequests, user.id]);

  // Determine default outcome type based on context
  const defaultOutcomeType = useMemo((): Section12PaymentRequestOutcomeType => {
    // Edit mode: initialise from the existing request's value
    if (editingRequest) {
      return editingRequest.outcomeType;
    }
    if (hints?.initialOutcomeType) {
      return hints.initialOutcomeType;
    }
    // If coming from a no-med-rec work item flow
    if (isNoMedRecFlow) {
      return "no-med-rec";
    }
    // If doctor has signed forms, default to form type
    if (doctorSignedForms.length > 0) {
      return "form";
    }
    // Otherwise default to completed-outside-thalamos
    return "completed-outside-thalamos";
  }, [
    editingRequest,
    hints?.initialOutcomeType,
    isNoMedRecFlow,
    doctorSignedForms.length,
  ]);

  // State
  const [outcomeType, setOutcomeType] =
    useState<Section12PaymentRequestOutcomeType>(defaultOutcomeType);
  // An edit that keeps its team is billed as it was raised, so it is priced
  // by that stored authority rather than by the team's routing today.
  const withEditedRequestRouting = (team: AmhpTeamOption): AmhpTeamOption =>
    editingRequest?.amhpTeam?.id === team.id
      ? {
          ...team,
          section12Invoicing:
            editingRequest.routingScenario === null
              ? null
              : {
                  localAuthorityId: editingRequest.invoicingLocalAuthorityId,
                  localAuthorityName:
                    editingRequest.invoicingLocalAuthorityName,
                  icbName: editingRequest.invoicingIcbName,
                  scenario: editingRequest.routingScenario,
                },
        }
      : team;

  const [selectedAmhpTeam, setSelectedAmhpTeam] =
    useState<AmhpTeamOption | null>(() => {
      const existingAmhpTeam = editingRequest?.amhpTeam;
      if (existingAmhpTeam) {
        const fromOptions = amhpTeams.find((t) => t.id === existingAmhpTeam.id);
        if (fromOptions) return withEditedRequestRouting(fromOptions);
        return withEditedRequestRouting({
          id: existingAmhpTeam.id,
          name: existingAmhpTeam.name,
          // Conservatively assume payments-enabled is true on the locked
          // existing team — if it wasn't, the request couldn't exist.
          section12PaymentsEnabled: true,
          section12PrecisePaymentsEnabled: true,
        });
      }
      return onlyAmhpTeam ?? null;
    });

  // The form context only answers for the teams it lists, so a team chosen
  // another way (an invited AMHP's) asks the server. Without the answer the
  // dialog would price with the patient's authority while the server bills
  // the routed one, and refuse the rate on submission.
  const getTeamInvoicing =
    apiOverride?.getTeamInvoicing ?? defaultApi.getTeamInvoicing;
  const selectedTeamId = selectedAmhpTeam?.id;
  const selectedTeamHasInvoicing =
    selectedAmhpTeam?.section12Invoicing !== undefined;
  useEffect(() => {
    if (selectedTeamId === undefined || selectedTeamHasInvoicing) {
      return;
    }
    let cancelled = false;
    // Null is stored too, so a team the server has no answer for stops holding
    // the dialog.
    void getTeamInvoicing(formContext.id, selectedTeamId).then((invoicing) => {
      if (cancelled) {
        return;
      }
      setSelectedAmhpTeam((current) =>
        current?.id === selectedTeamId
          ? { ...current, section12Invoicing: invoicing }
          : current,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [
    getTeamInvoicing,
    selectedTeamId,
    selectedTeamHasInvoicing,
    formContext.id,
  ]);
  const [selectedLocalAuthority, setSelectedLocalAuthority] =
    useState<SelectedLocalAuthority>(() => {
      if (editingRequest && editingRequest.localAuthority) {
        return {
          id: editingRequest.localAuthority.id,
          name: editingRequest.localAuthority.name,
        };
      }
      if (editingRequest?.localAuthorityName) {
        return { name: editingRequest.localAuthorityName };
      }
      return null;
    });
  // "Local authority which arranged this assessment" — the doctor's own input,
  // distinct from the (system-derived) responsible-for-payment LA above.
  // Captured on guest-AMHP invites.
  const [arrangingLocalAuthority, setArrangingLocalAuthority] =
    useState<SelectedLocalAuthority>(() => {
      if (editingRequest?.arrangingLocalAuthorityId) {
        return {
          id: editingRequest.arrangingLocalAuthorityId,
          name: editingRequest.arrangingLocalAuthorityName ?? "",
        };
      }
      if (editingRequest?.arrangingLocalAuthorityName) {
        return { name: editingRequest.arrangingLocalAuthorityName };
      }
      return null;
    });
  const [selectedFormId, setSelectedFormId] = useState<string | null>(
    // Edit mode: form selection is locked
    editingRequest?.formId ??
      // Use hint if it is still on offer, otherwise auto-select if only one form
      doctorSignedForms.find((f) => f.formId === hints?.initialFormId)
        ?.formId ??
      onlyDoctorSignedForm?.formId ??
      null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isTrainingFormContext = formContext.isTrainingFormContext === true;

  // --- AMHP invite state (used when no AMHP teams are available) ---
  // In edit mode, pre-fill the typeahead from the existing payment request's
  // AMHP user so the doctor can see who was assigned (and swap if needed).
  const initialInvitedAmhpUser = useMemo<UserSearchResult | null>(() => {
    if (!editingRequest?.amhpUser) return null;
    const amhpUser = editingRequest.amhpUser;
    return {
      id: amhpUser.id,
      name: amhpUser.name,
      email: amhpUser.email,
      type: "user" as const,
      contexts: amhpUser.amhpTeams.map((t) => ({
        id: t.id,
        name: t.name,
        email: "",
        teamType: "amhp" as const,
        type: "team" as const,
        role: "member" as const,
        isTrainingOrganisation: t.isTrainingOrganisation ?? false,
        dataControllerId: null,
        dataControllerName: null,
        section12PaymentsEnabled: t.section12PaymentsEnabled,
        section12PrecisePaymentsEnabled: t.section12PrecisePaymentsEnabled,
      })),
    };
  }, [editingRequest?.amhpUser]);
  const [amhpSearchResults, setAmhpSearchResults] = useState<
    UserSearchResult[]
  >([]);
  const [amhpSearchLoading, setAmhpSearchLoading] = useState(false);
  const [amhpSearchInputValue, setAmhpSearchInputValue] = useState<string>(
    initialInvitedAmhpUser
      ? `${initialInvitedAmhpUser.name} (${initialInvitedAmhpUser.email})`
      : "",
  );
  const [invitedAmhpUser, setInvitedAmhpUser] =
    useState<UserSearchResult | null>(initialInvitedAmhpUser);
  // Full Name captured when inviting an unregistered (guest) AMHP.
  const [guestFullName, setGuestFullName] = useState<string>("");
  const amhpSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up the debounce timer on unmount to prevent setting state after the
  // component has been removed from the tree.
  useEffect(() => {
    return () => {
      if (amhpSearchTimerRef.current) {
        clearTimeout(amhpSearchTimerRef.current);
      }
    };
  }, []);

  const handleAmhpSearchInputChange = useCallback(
    (value: string) => {
      setAmhpSearchInputValue(value);

      // If the value matches a selected user label, ignore (selection event)
      if (
        invitedAmhpUser &&
        value === `${invitedAmhpUser.name} (${invitedAmhpUser.email})`
      ) {
        return;
      }

      // Clear selection when user types over it
      if (invitedAmhpUser) {
        setInvitedAmhpUser(null);
        setSelectedAmhpTeam(null);
      }

      if (amhpSearchTimerRef.current) {
        clearTimeout(amhpSearchTimerRef.current);
      }

      if (value.length < 3) {
        setAmhpSearchResults([]);
        return;
      }

      amhpSearchTimerRef.current = setTimeout(async () => {
        setAmhpSearchLoading(true);
        try {
          const response = await api.section12.searchAmhpUsers(value, 10, 0);
          if (response.status === 200) {
            setAmhpSearchResults(
              filterAmhpSearchResultsByTrainingForm(
                response.data.results,
                isTrainingFormContext,
              ),
            );
          }
        } catch {
          setAmhpSearchResults([]);
        } finally {
          setAmhpSearchLoading(false);
        }
      }, 200);
    },
    [invitedAmhpUser, isTrainingFormContext],
  );

  const handleAmhpUserSelected = useCallback(
    (value: UserSearchResult | string | null) => {
      if (!value || typeof value === "string") {
        setInvitedAmhpUser(null);
        setSelectedAmhpTeam(null);
        return;
      }

      setInvitedAmhpUser(value);

      // Resolve the AMHP team from the user's contexts
      const amhpContexts = filterAmhpContextsByTrainingForm(
        value.contexts,
        isTrainingFormContext,
      );

      const onlyAmhpContext =
        amhpContexts.length === 1 ? amhpContexts[0] : undefined;
      if (onlyAmhpContext !== undefined) {
        // Auto-select the single AMHP team
        setSelectedAmhpTeam({
          id: onlyAmhpContext.id,
          name: onlyAmhpContext.name,
          // Read the payments flag from the API response; absent means unknown,
          // which we treat as false so the banner is shown conservatively.
          section12PaymentsEnabled:
            onlyAmhpContext.section12PaymentsEnabled === true,
          section12PrecisePaymentsEnabled:
            onlyAmhpContext.section12PrecisePaymentsEnabled === true,
        });
      } else if (amhpContexts.length > 1) {
        // Multiple teams — let user pick; clear any previous selection
        setSelectedAmhpTeam(null);
      }
    },
    [isTrainingFormContext],
  );

  // When an invited AMHP has multiple AMHP teams, derive the list for selection
  const invitedAmhpTeamOptions = useMemo((): AmhpTeamOption[] => {
    if (!invitedAmhpUser) return [];
    return filterAmhpContextsByTrainingForm(
      invitedAmhpUser.contexts,
      isTrainingFormContext,
    ).map((c) => ({
      id: c.id,
      name: c.name,
      // Read the payments flag from the API response; absent means unknown,
      // which we treat as false so the banner is shown conservatively.
      section12PaymentsEnabled: c.section12PaymentsEnabled === true,
      section12PrecisePaymentsEnabled:
        c.section12PrecisePaymentsEnabled === true,
    }));
  }, [invitedAmhpUser, isTrainingFormContext]);

  // Step state for three-page flow
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(
    isLimitedEdit ? 2 : 1,
  );

  // Step 3 declarations — the doctor must affirm the combined declaration
  // before submitting. Reset on each open (including edit/resubmit) so the
  // doctor re-confirms.
  const [declarationsAccepted, setDeclarationsAccepted] = useState(false);
  const allDeclarationsConfirmed = declarationsAccepted;

  // Guest accreditation — captured inline for guest users
  const isGuest = isGuestUserSession(user);
  const [guestAccreditation, setGuestAccreditation] =
    useState<GuestSessionAccreditation | null>(null);
  const [guestGateChecked, setGuestGateChecked] = useState(!isGuest);

  useEffect(() => {
    if (!isGuest) return;
    let cancelled = false;
    api.users
      .getGuestSessionAccreditation()
      .then(assertOkResponse)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.gmcNumber) {
          setGuestAccreditation(res.data);
        }
        setGuestGateChecked(true);
      })
      .catch(() => {
        if (!cancelled) setGuestGateChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isGuest]);

  // "No med rec" inline reason dialog state
  const [showNoMedRecDialog, setShowNoMedRecDialog] = useState(false);
  const [isRecordingReason, setIsRecordingReason] = useState(false);
  // In edit mode, pre-fill from the existing record so the doctor doesn't have
  // to re-enter the reason just to keep it.
  const [capturedNoMedRecReason, setCapturedNoMedRecReason] = useState<
    string | undefined
  >(editingRequest?.noMedRecReason ?? undefined);
  // Why the reason could not be recorded. Shown on the reason dialog itself,
  // which stays open so the doctor keeps the answer they just gave.
  const [noMedRecRefusal, setNoMedRecRefusal] = useState<string | undefined>(
    undefined,
  );

  // Assessment date/time — pre-populated from the earliest interview time range
  // Sort interviews by their first time range start to find the most relevant one,
  // rather than relying on the order interviews are returned by the API.
  const firstInterviewTimeRangeStart = useMemo((): string | null => {
    const interviews = formContext.interviews ?? [];
    if (interviews.length === 0) return null;

    // Collect all time range starts across all interviews
    const starts = interviews
      .flatMap((iv) => iv.timeRanges.map((tr) => tr.start))
      .filter(Boolean);

    if (starts.length === 0) return null;

    // Return the earliest start time
    return starts.reduce((earliest, current) =>
      current < earliest ? current : earliest,
    );
  }, [formContext.interviews]);
  const defaultAssessmentDateTime = useMemo((): Dayjs | null => {
    if (editingRequest?.assessmentDateTime) {
      return dayjs(editingRequest.assessmentDateTime).tz(UK_TIME_ZONE);
    }
    if (firstInterviewTimeRangeStart) {
      return dayjs(firstInterviewTimeRangeStart).tz(UK_TIME_ZONE);
    }
    return null;
  }, [editingRequest?.assessmentDateTime, firstInterviewTimeRangeStart]);

  const [assessmentDateTime, setAssessmentDateTime] = useState<Dayjs | null>(
    defaultAssessmentDateTime,
  );
  const [
    hasAssessmentDateTimeFutureValidationError,
    setHasAssessmentDateTimeFutureValidationError,
  ] = useState(false);
  const [
    hasAssessmentDateTimePastValidationError,
    setHasAssessmentDateTimePastValidationError,
  ] = useState(false);

  // Sync assessmentDateTime when interviews load after initial render
  useEffect(() => {
    if (assessmentDateTime === null && defaultAssessmentDateTime !== null) {
      setAssessmentDateTime(defaultAssessmentDateTime);
    }
  }, [assessmentDateTime, defaultAssessmentDateTime]);

  // Assessment location — prefill from the editing request when present, else
  // from the first non-unknown interview location set during the doctor-finder
  // flow. Doctor can edit before submit. NULL for legacy/admin-created rows.
  const defaultAssessmentLocation = useMemo((): {
    address: string;
    postalCode: string;
  } => {
    if (
      editingRequest?.assessmentLocationAddress ||
      editingRequest?.assessmentLocationPostcode
    ) {
      return {
        address: editingRequest.assessmentLocationAddress ?? "",
        postalCode: editingRequest.assessmentLocationPostcode ?? "",
      };
    }
    const interview = (formContext.interviews ?? []).find(
      (iv) => !iv.locationUnknown && (iv.address || iv.postalCode),
    );
    return {
      address: interview?.address ?? "",
      postalCode: interview?.postalCode ?? "",
    };
  }, [
    editingRequest?.assessmentLocationAddress,
    editingRequest?.assessmentLocationPostcode,
    formContext.interviews,
  ]);

  const [assessmentLocationAddress, setAssessmentLocationAddress] = useState(
    defaultAssessmentLocation.address,
  );
  const [assessmentLocationPostcode, setAssessmentLocationPostcode] = useState(
    defaultAssessmentLocation.postalCode,
  );

  // Sync assessment location when interviews load after initial render and the
  // doctor hasn't typed anything yet.
  useEffect(() => {
    if (
      !assessmentLocationAddress &&
      !assessmentLocationPostcode &&
      (defaultAssessmentLocation.address ||
        defaultAssessmentLocation.postalCode)
    ) {
      setAssessmentLocationAddress(defaultAssessmentLocation.address);
      setAssessmentLocationPostcode(defaultAssessmentLocation.postalCode);
    }
  }, [
    assessmentLocationAddress,
    assessmentLocationPostcode,
    defaultAssessmentLocation.address,
    defaultAssessmentLocation.postalCode,
  ]);

  // Invoice items state — pre-populated from editingRequest when editing.
  // The dialog operates on a flat set of fields per item-type rather than
  // a generic "items" array, so we destructure the existing items here.
  const editingConfiguredRateItem = editingRequest?.items.find(
    (it) => it.itemType === "configured-rate",
  );
  const editingMileageItem = editingRequest?.items.find(
    (it) => it.itemType === "mileage",
  );
  const editingOtherItem = editingRequest?.items.find(
    (it) => it.itemType === "other",
  );

  const [selectedConfiguredRateKey, setSelectedConfiguredRateKey] =
    useState<string>(
      editingConfiguredRateItem?.configuredRateKey &&
        editingConfiguredRateItem.configuredRateKey !== ESTIMATED_RATE_KEY
        ? editingConfiguredRateItem.configuredRateKey
        : "",
    );
  const [mileageDistance, setMileageDistance] = useState<string>(
    editingMileageItem?.mileageDistanceMiles != null
      ? String(editingMileageItem.mileageDistanceMiles)
      : "",
  );
  const [mileageStartAddress, setMileageStartAddress] = useState<{
    address: string;
    postalCode: string;
  }>({
    address: editingMileageItem?.startLocation ?? "",
    postalCode: editingMileageItem?.startLocationPostcode ?? "",
  });
  const [otherAmountPounds, setOtherAmountPounds] = useState<string>(
    editingOtherItem ? (editingOtherItem.amountPence / 100).toFixed(2) : "",
  );
  const [otherDescription, setOtherDescription] = useState<string>(
    editingOtherItem?.description ?? "",
  );
  const [expenseReceiptFiles, setExpenseReceiptFiles] = useState<
    UploadedFile[]
  >(() => {
    if (!editingOtherItem) return [];
    // Map existing receipts onto the UploadedFile shape the dialog uses.
    // base64/file are placeholders because the file isn't re-fetched in the
    // browser; uploadedKey is what we round-trip on save.
    return editingOtherItem.receipts.map((receipt) => {
      const mimeType =
        receipt.fileType === "pdf"
          ? "application/pdf"
          : receipt.fileType === "png"
            ? "image/png"
            : receipt.fileType === "webp"
              ? "image/webp"
              : "image/jpeg";
      return {
        name: receipt.fileName,
        type: mimeType,
        size: 0,
        base64: "",
        file: new File([], receipt.fileName, { type: mimeType }),
        uploadedKey: receipt.uploadedKey,
      };
    });
  });
  const [expenseReceiptError, setExpenseReceiptError] = useState<string | null>(
    null,
  );
  const [isUploadingReceipts, setIsUploadingReceipts] = useState(false);

  // The authority whose rates apply and whose reference the request takes. It
  // is the patient's own unless the board organising the assessment pays for
  // out-of-area work, which only the server can tell us. Falling back to the
  // patient's covers a team with no routing answer and the guest-invite
  // path, which has no team to resolve from.
  const invoicingLocalAuthorityId =
    selectedAmhpTeam?.section12Invoicing?.localAuthorityId ??
    selectedLocalAuthority?.id;

  const selectedLaPaymentConfiguration = useMemo(() => {
    if (!invoicingLocalAuthorityId) {
      return null;
    }
    const localAuthority = managedLocationsContext.managedLocations.find(
      (loc) => loc.id === invoicingLocalAuthorityId,
    );
    if (!localAuthority?.paymentRates?.length) {
      return null;
    }

    return {
      paymentRates: localAuthority.paymentRates,
      mileageRatePencePerMile: localAuthority.mileageRatePencePerMile ?? 0,
      otherItemLimitPence: localAuthority.otherItemLimitPence ?? null,
      laName: localAuthority.name,
    };
  }, [invoicingLocalAuthorityId, managedLocationsContext.managedLocations]);

  // In estimated mode (no LA-configured rates) these collapse to inert values
  // — every consumer that *displays* them is gated on isPrecisePayments.
  const effectiveRates = useMemo(
    () => ({
      paymentRates: selectedLaPaymentConfiguration?.paymentRates ?? [],
      mileageRatePencePerMile:
        selectedLaPaymentConfiguration?.mileageRatePencePerMile ?? 0,
      otherItemLimitPence:
        selectedLaPaymentConfiguration?.otherItemLimitPence ?? null,
    }),
    [selectedLaPaymentConfiguration],
  );

  const selectedConfiguredRate = useMemo(
    () =>
      effectiveRates.paymentRates.find(
        (rate) => rate.key === selectedConfiguredRateKey,
      ) ?? null,
    [effectiveRates.paymentRates, selectedConfiguredRateKey],
  );

  // Reset the configured rate selection whenever the paying authority changes,
  // including when switching team moves it.
  const [prevLocalAuthorityId, setPrevLocalAuthorityId] = useState(
    invoicingLocalAuthorityId,
  );
  if (prevLocalAuthorityId !== invoicingLocalAuthorityId) {
    setPrevLocalAuthorityId(invoicingLocalAuthorityId);
    setSelectedConfiguredRateKey("");
  }

  // Check if the selected LA has a 'call-out-fee' configured rate
  const calloutFeeRate = useMemo(
    () =>
      effectiveRates.paymentRates.find((r) => r.key === "call-out-fee") ?? null,
    [effectiveRates.paymentRates],
  );

  // When outcome type changes to 'callout-fee', auto-select the call-out-fee rate
  useEffect(() => {
    if (outcomeType === "call-out-fee" && calloutFeeRate) {
      setSelectedConfiguredRateKey(calloutFeeRate.key);
    }
  }, [outcomeType, calloutFeeRate]);

  // --- Guest AMHP invite (behind the doctor-org feature flag) ---
  // A full doctor may invite an unregistered AMHP only when no AMHP team is
  // assigned to the work item and the org has the capability enabled. The
  // server re-enforces all of this (and rejects guest users outright).
  //
  // "Assigned" must match the server's rule: any AMHP work item in
  // pending/accepted/completed counts (a pending invite is a live assignment).
  // This is deliberately broader than `amhpTeams` (accepted/completed only,
  // used for the selectable-team dropdown) — gating the guest path on
  // `amhpTeams.length === 0` would offer the flow when a *pending* AMHP invite
  // exists, only for the server to reject it.
  const hasActiveAmhpWorkItem = formContext.workItems.some(
    (wi) =>
      wi.team.type === "amhp" &&
      ["pending", "accepted", "completed"].includes(wi.status),
  );
  const canInviteGuestAmhp =
    !isGuest &&
    !isEditMode &&
    !hasActiveAmhpWorkItem &&
    user.sessionOrganisationConfiguration != null &&
    canDoctorInviteGuestS12Amhp(user.sessionOrganisationConfiguration);

  // The doctor has typed a valid email that matches no registered AMHP result.
  // This is a UI heuristic only — the server confirms the address is not a
  // full user when it calls createOrGetGuestUser.
  // A typed email that matches no registered AMHP result switches the dialog
  // into guest-invite mode. UI heuristic only — the server is the source of
  // truth for validity and whether the address is actually a guest.
  const trimmedAmhpSearch = amhpSearchInputValue.trim().toLowerCase();
  const amhpSearchLooksLikeEmail = isValidEmail(trimmedAmhpSearch);
  const matchesRegisteredAmhp = amhpSearchResults.some(
    (r) => r.email.toLowerCase() === trimmedAmhpSearch,
  );
  const guestInviteMode =
    canInviteGuestAmhp &&
    !invitedAmhpUser &&
    !amhpSearchLoading &&
    amhpSearchLooksLikeEmail &&
    !matchesRegisteredAmhp;
  // A guest invite also requires the doctor to state which local authority
  // arranged the assessment (a name, with or without a managed-location match).
  const guestInviteValid =
    guestInviteMode &&
    guestFullName.trim().length > 0 &&
    (arrangingLocalAuthority?.name?.trim().length ?? 0) > 0;

  // Look up the local authority from the patient's GP ODS code (always)
  const [{ data: resolvedLa, loading: laLoading, error: laError }] =
    apiHooks.ods.getLocalAuthorityByOdsCode(
      formContext.patient.registeredGpOdsCode ?? null,
    );

  // Skipped in edit mode — the existing request's LA is the source of truth.
  // This is the patient's own authority, which the request records for audit
  // and which is system-derived (never doctor-selectable), including on the
  // guest-invite path. Which authority is *billed* can differ, and is resolved
  // server-side — see `invoicingLocalAuthority`.
  useEffect(() => {
    if (isEditMode) return;
    if (laLoading) return;

    if (!resolvedLa) {
      setSelectedLocalAuthority(null);
      return;
    }

    // Names are unique only within a type, so matching on name alone can
    // select a board rather than the authority that pays.
    const matched = managedLocationsContext.managedLocations.find(
      (loc) =>
        loc.type === "localAuthority" &&
        (loc.odsCode === resolvedLa.code ||
          loc.name.toLowerCase() === resolvedLa.name?.toLowerCase()),
    );

    if (matched) {
      setSelectedLocalAuthority({ id: matched.id, name: matched.name });
    } else {
      // No managed local authority match — store name only for estimated fees
      setSelectedLocalAuthority({
        name: resolvedLa.name ?? resolvedLa.code,
      });
    }
  }, [
    isEditMode,
    resolvedLa,
    laLoading,
    managedLocationsContext.managedLocations,
    setSelectedLocalAuthority,
  ]);

  // Parsed mileage values — single source of truth for distance and amount
  const { distanceMiles, mileagePence } = useMemo(() => {
    const parsed = parseFloat(mileageDistance || "0");
    const miles = !isNaN(parsed) && parsed > 0 ? parsed : 0;
    const pence =
      miles > 0
        ? Math.round(miles * effectiveRates.mileageRatePencePerMile)
        : 0;
    return { distanceMiles: miles, mileagePence: pence };
  }, [mileageDistance, effectiveRates.mileageRatePencePerMile]);

  // Calculate total amount in pence using effective rates
  const totalAmountPence = useMemo(() => {
    let total = selectedConfiguredRate?.amountPence ?? 0;

    total += mileagePence;

    const otherPence = Math.round(parseFloat(otherAmountPounds || "0") * 100);
    if (!Number.isNaN(otherPence) && otherPence > 0) {
      total += otherPence;
    }

    return total;
  }, [selectedConfiguredRate, mileagePence, otherAmountPounds]);

  // Format pence as pounds for display
  const formatPounds = (pence: number): string => {
    return `£${(pence / 100).toFixed(2)}`;
  };

  // Auto-select form when switching to 'form' outcome type and there's only one form
  useEffect(() => {
    if (
      outcomeType === "form" &&
      onlyDoctorSignedForm !== undefined &&
      !selectedFormId
    ) {
      setSelectedFormId(onlyDoctorSignedForm.formId);
    }
  }, [outcomeType, onlyDoctorSignedForm, selectedFormId]);

  // Validation
  const hasAmhpTeam = !!selectedAmhpTeam?.id;
  const selectedAmhpTeamHasSection12PaymentsEnabled =
    selectedAmhpTeam?.section12PaymentsEnabled === true;
  // Two journeys only: precise (LA-configured rates and team flag on) or
  // estimated (anything else). Estimated mode hides the fee selection and
  // total and submits items with the ESTIMATED_RATE_KEY sentinel at 0p.
  const matchedManagedLocation = useMemo(
    () =>
      invoicingLocalAuthorityId
        ? (managedLocationsContext.managedLocations.find(
            (loc) => loc.id === invoicingLocalAuthorityId,
          ) ?? null)
        : null,
    [invoicingLocalAuthorityId, managedLocationsContext.managedLocations],
  );
  const isPrecisePayments =
    selectedAmhpTeam?.section12PrecisePaymentsEnabled === true &&
    !!invoicingLocalAuthorityId &&
    !!matchedManagedLocation?.paymentRates?.length;

  // Rates from an authority other than the patient's own. Who pays is kept from
  // doctors, so the dialog names no authority behind these rates, and an
  // unidentified patient authority does not mean estimated fees.
  const pricedByAnotherAuthority =
    isPrecisePayments &&
    invoicingLocalAuthorityId !== selectedLocalAuthority?.id;

  const isReconciliationEnabled =
    !!user.sessionOrganisationConfiguration &&
    hasAspireReconciliationFlag(user.sessionOrganisationConfiguration);

  // Form selection is only needed for 'form' outcome type
  const hasValidFormSelection = useMemo(() => {
    if (outcomeType !== "form") {
      return true; // No form needed for other outcome types
    }
    if (doctorSignedForms.length === 0) {
      return false; // No forms available for form-based flow
    }
    return !!selectedFormId;
  }, [outcomeType, doctorSignedForms.length, selectedFormId]);

  // Check if this is a duplicate request. Skipped in edit mode — the doctor
  // is by definition editing the existing request, not creating a new one.
  const duplicateOf = useMemo(():
    PriorRequestLink | "same-assessment" | undefined => {
    if (isEditMode) return undefined;
    if (outcomeType === "form" && selectedFormId) {
      return priorRequestLinkByFormId.get(selectedFormId);
    }
    // For non-form types, check if any request exists without a formId
    return hasExistingNoMedRecPaymentRequest ? "same-assessment" : undefined;
  }, [
    isEditMode,
    outcomeType,
    selectedFormId,
    priorRequestLinkByFormId,
    hasExistingNoMedRecPaymentRequest,
  ]);
  const isDuplicateRequest = duplicateOf !== undefined;

  const hasOther =
    otherAmountPounds !== "" && parseFloat(otherAmountPounds) > 0;
  const hasUploadedOtherReceipt = expenseReceiptFiles.some(
    (file) => "uploadedKey" in file && !!file.uploadedKey,
  );
  const otherReceiptRequiredError =
    hasOther && !hasUploadedOtherReceipt
      ? "Please upload at least one receipt for the Other expense."
      : null;
  const otherAmountPence = Math.round(
    parseFloat(otherAmountPounds || "0") * 100,
  );
  const otherItemExceedsLimit =
    !Number.isNaN(otherAmountPence) &&
    otherAmountPence > 0 &&
    effectiveRates.otherItemLimitPence !== null &&
    otherAmountPence > effectiveRates.otherItemLimitPence;

  // Check whether the mileage section has a start location when required
  const hasMileage = distanceMiles > 0;
  const hasMileageStartLocation = mileageStartAddress.address.trim() !== "";

  const handleAssessmentDateTimeChange = (value: Dayjs | null) => {
    setAssessmentDateTime(
      value?.isValid() ? ukInstantOfWallClock(value) : value,
    );
  };

  // Assessment date/time must be a valid, populated value and not be in the future
  const handleAssessmentDateTimeError = (error: unknown) => {
    // onError reports a single code (or null) at a time, so set both flags on
    // every call — otherwise switching between a past and future error (or
    // clearing it) would leave a stale flag set.
    const code = typeof error === "string" ? error : null;
    setHasAssessmentDateTimeFutureValidationError(
      code !== null && ASSESSMENT_DATE_TIME_FUTURE_ERROR_CODES.has(code),
    );
    setHasAssessmentDateTimePastValidationError(
      code !== null && ASSESSMENT_DATE_TIME_PAST_ERROR_CODES.has(code),
    );
  };
  // We retain both checks because picker onError captures manual text-entry validation,
  // while the dayjs() comparison guards programmatic/default values that may bypass picker events.
  const isAssessmentDateTimeInFuture =
    hasAssessmentDateTimeFutureValidationError ||
    (assessmentDateTime !== null &&
      assessmentDateTime.isValid() &&
      assessmentDateTime.isAfter(dayjs()));
  // Limited edits (receipt-missing / value-incorrect) never render the date
  // field and submit items only, so a stored date that legitimately predates the
  // earliest allowed date must not block them. The backstop uses a day-level
  // comparison to match MUI's minDate (isBeforeDay), so it agrees with the
  // picker on the boundary: the earliest allowed day itself is accepted.
  const isAssessmentDateTimeTooFarInPast =
    !isLimitedEdit &&
    (hasAssessmentDateTimePastValidationError ||
      (assessmentDateTime !== null &&
        assessmentDateTime.isValid() &&
        assessmentDateTime.isBefore(earliestAssessmentDate, "day")));
  const hasValidAssessmentDateTime =
    assessmentDateTime !== null &&
    assessmentDateTime.isValid() &&
    !isAssessmentDateTimeInFuture &&
    !isAssessmentDateTimeTooFarInPast;

  // The recipient is valid when either an AMHP team is selected, or a guest
  // AMHP invite has been entered with a name (guest mode is always estimated).
  const hasValidRecipient = guestInviteMode ? guestInviteValid : hasAmhpTeam;

  // Rates depend on the team's billing authority, so neither step proceeds
  // until it is known.
  const teamInvoicingPending =
    selectedAmhpTeam !== null &&
    selectedAmhpTeam.section12Invoicing === undefined;

  const canSubmit =
    hasValidRecipient &&
    !laLoading &&
    !teamInvoicingPending &&
    hasValidFormSelection &&
    hasValidAssessmentDateTime &&
    (isPrecisePayments ? !!selectedConfiguredRate : true) &&
    !otherReceiptRequiredError &&
    !otherItemExceedsLimit &&
    (!hasMileage || hasMileageStartLocation) &&
    !isSubmitting &&
    !isUploadingReceipts;

  const hasValidAssessmentLocation =
    assessmentLocationAddress.trim().length > 0 &&
    assessmentLocationPostcode.trim().length > 0;

  // Validation for step 1 (can proceed to step 2). For a guest invite there is
  // no AMHP-team payments flag to check — the guest invite validity stands in.
  const recipientValidForStep2 = guestInviteMode
    ? guestInviteValid
    : hasAmhpTeam && selectedAmhpTeamHasSection12PaymentsEnabled;

  const canProceedToStep2 =
    recipientValidForStep2 &&
    !laLoading &&
    !teamInvoicingPending &&
    hasValidFormSelection &&
    hasValidAssessmentDateTime &&
    hasValidAssessmentLocation;

  const isBusy = isSubmitting || isUploadingReceipts;

  // Check if user is an S12 doctor (has doctor accreditation)
  const hasDoctorAccreditation = user.accreditations.some(
    isDoctorAccreditation,
  );
  const canSubmitAsGuest = isGuestDoctorForFormContext(user, formContext);
  if (!hasDoctorAccreditation && !canSubmitAsGuest) {
    return null;
  }

  // Guest users who haven't yet provided their accreditation see the gate dialog
  if (isGuest && guestGateChecked && !guestAccreditation) {
    return (
      <GuestAccreditationGateDialog
        onAccreditationSubmitted={(accreditation) => {
          setGuestAccreditation(accreditation);
        }}
      />
    );
  }

  const getReceiptFileType = (file: UploadedFile) => {
    const mimeType = file.type;

    if (mimeType === "application/pdf") {
      return "pdf" as const;
    }
    if (mimeType === "image/png") {
      return "png" as const;
    }
    if (mimeType === "image/webp") {
      return "webp" as const;
    }

    return "jpg" as const;
  };

  const uploadReceiptFile = async (
    file: UploadedFile,
  ): Promise<UploadedReceiptFile> => {
    const base64Data = file.base64 as string;
    const binaryData = base64.parse(base64PayloadOf(base64Data));
    const hash = md5.base64(binaryData);
    const presignedResponse = await api.system.generatePresignedUploadUrl({
      fileName: file.name,
      contentMd5: hash,
    });

    if (presignedResponse.status !== 200) {
      throw new Error("Failed to generate presigned upload URL");
    }

    const uploadResponse = await axios.request({
      method: "PUT",
      url: presignedResponse.data.uploadUrl,
      data: binaryData,
      headers: {
        "Content-Type": file.type,
        "Content-MD5": hash,
      },
      validateStatus: () => true,
    });

    if (uploadResponse.status !== 200) {
      throw new Error("Failed to upload receipt file");
    }

    return {
      ...file,
      uploadedKey: presignedResponse.data.key,
    };
  };

  const handleReceiptFilesSelected = async (files: UploadedFile[]) => {
    const nextFileCount = expenseReceiptFiles.length + files.length;
    if (nextFileCount > 10) {
      setExpenseReceiptError("You can upload up to 10 receipt files.");
      return;
    }

    for (const file of files) {
      if (!RECEIPT_VALID_MIME_TYPES.includes(file.type)) {
        setExpenseReceiptError(
          "Receipts must be a PDF, JPG, PNG or WEBP file.",
        );
        return;
      }

      if (file.size > RECEIPT_MAX_FILE_SIZE_BYTES) {
        setExpenseReceiptError("Each receipt must be 5MB or smaller.");
        return;
      }
    }

    setExpenseReceiptError(null);
    setIsUploadingReceipts(true);

    try {
      const uploadResults = await Promise.allSettled(
        files.map((file) => uploadReceiptFile(file)),
      );

      const successfulUploads = uploadResults
        .filter(
          (result): result is PromiseFulfilledResult<UploadedReceiptFile> =>
            result.status === "fulfilled",
        )
        .map((result) => result.value);

      if (successfulUploads.length > 0) {
        setExpenseReceiptFiles((currentFiles) => [
          ...currentFiles,
          ...successfulUploads,
        ]);
      }

      if (successfulUploads.length !== uploadResults.length) {
        setExpenseReceiptError(
          "Some receipts failed to upload. Please try those files again.",
        );
        renderErrorToast({
          message: "Some receipts failed to upload. Please try again.",
        });
      }
    } catch (error) {
      console.error("Failed to upload receipt files:", error);
      setExpenseReceiptError(
        "Failed to upload receipt files. Please try again.",
      );
      renderErrorToast({ message: "Failed to upload receipt files" });
    } finally {
      setIsUploadingReceipts(false);
    }
  };

  const handleRemoveReceipt = (index: number) => {
    setExpenseReceiptFiles((currentFiles) =>
      currentFiles.filter((_, i) => i !== index),
    );
  };

  const handleDialogClose = () => {
    if (isBusy) {
      return;
    }

    onClose();
  };

  const handleSubmit = async () => {
    // A guest invite has no selectedAmhpTeam — guestInviteValid stands in.
    if (!canSubmit) return;
    if (!guestInviteMode && !selectedAmhpTeam) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Build invoice items array
      const items: CreateSection12PaymentRequestItemInput[] = [];

      if (isPrecisePayments) {
        if (!selectedConfiguredRate) {
          throw new Error("No configured rate selected");
        }

        // Configured fee item (always required)
        items.push({
          itemType: "configured-rate",
          configuredRateKey: selectedConfiguredRate.key,
          configuredRateLabel: selectedConfiguredRate.label,
          amountPence: selectedConfiguredRate.amountPence,
          description: selectedConfiguredRate.label,
        });

        // Mileage item (optional)
        if (hasMileage) {
          items.push({
            itemType: "mileage",
            amountPence: mileagePence,
            description: `Travel expenses (${mileageDistance} miles)`,
            mileageDistanceMiles: distanceMiles,
            startLocation: mileageStartAddress.address.trim() || undefined,
            startLocationPostcode:
              mileageStartAddress.postalCode.trim() || undefined,
          });
        }
      } else {
        // Estimated mode — nominal 0p values
        items.push({
          itemType: "configured-rate",
          configuredRateKey: ESTIMATED_RATE_KEY,
          configuredRateLabel: ESTIMATED_RATE_LABEL,
          amountPence: 0,
          description: ESTIMATED_RATE_LABEL,
        });

        if (hasMileage) {
          items.push({
            itemType: "mileage",
            amountPence: 0,
            description: `Travel expenses (${mileageDistance} miles)`,
            mileageDistanceMiles: distanceMiles,
            startLocation: mileageStartAddress.address.trim() || undefined,
            startLocationPostcode:
              mileageStartAddress.postalCode.trim() || undefined,
          });
        }
      }

      const otherPence = Math.round(parseFloat(otherAmountPounds || "0") * 100);
      if (!Number.isNaN(otherPence) && otherPence > 0) {
        const uploadedReceipts = expenseReceiptFiles
          .filter((file) => file.uploadedKey)
          .flatMap((file) => {
            if (!file.uploadedKey) {
              return [];
            }

            return [
              {
                fileName: file.name,
                fileType: getReceiptFileType(file),
                uploadedKey: file.uploadedKey,
              },
            ];
          });

        items.push({
          itemType: "other",
          amountPence: otherPence,
          description: otherDescription || "Other",
          receipts: uploadedReceipts,
        });
      }

      if (editingRequest) {
        if (isLimitedEdit) {
          // Limited edit: only items are accepted. The server preserves
          // status, AMHP team, LA, outcome, etc., and (for receipt-missing)
          // rejects any item amount/rate/mileage changes — only receipts
          // may differ from the stored values.
          await apiClient.limitedUpdatePaymentRequest(editingRequest.id, {
            items,
          });
          renderSuccessToast({
            message:
              limitedScope === "receipt-missing"
                ? "Receipts updated"
                : "Payment request updated",
          });
        } else {
          // Editing always targets an existing AMHP team (guest invites only
          // happen on the create path), so a team must be selected here.
          if (!selectedAmhpTeam) {
            throw new Error("No AMHP team selected");
          }
          const updateData: UpdateSection12PaymentRequestInput = {
            amhpTeamId: selectedAmhpTeam.id,
            localAuthorityId: selectedLocalAuthority?.id,
            localAuthorityOdsCode: resolvedLa?.code,
            localAuthorityName:
              selectedLocalAuthority?.name ?? resolvedLa?.name ?? undefined,
            outcomeType,
            items,
            ...(outcomeType === "form" && selectedFormId
              ? { formId: selectedFormId }
              : {}),
            noMedRecReason:
              outcomeType === "no-med-rec"
                ? (capturedNoMedRecReason ?? recordedNoMedRecReason)
                : undefined,
            assessmentDateTime: assessmentDateTime
              ? assessmentDateTime.toISOString()
              : undefined,
            assessmentLocationAddress:
              assessmentLocationAddress.trim() || undefined,
            assessmentLocationPostcode:
              assessmentLocationPostcode.trim() || undefined,
            // Reaching submit requires ticking all declarations on step 3.
            // The server stamps the authoritative confirmation time.
            declarationsConfirmed: true,
          };

          await apiClient.updatePaymentRequest(editingRequest.id, updateData);
          renderSuccessToast({
            message:
              editingRequest.status === "rejected"
                ? "Payment request resubmitted for review"
                : "Payment request updated",
          });
        }
      } else {
        // Recipient: either a guest AMHP invite (no team up front — the server
        // creates the guest user/team) or an existing/selected AMHP team.
        const recipient: Pick<
          CreateSection12PaymentRequestInput,
          "amhpTeamId" | "invitedAmhpUserId" | "guestAmhpInvite"
        > = guestInviteMode
          ? {
              guestAmhpInvite: {
                email: trimmedAmhpSearch,
                fullName: guestFullName.trim(),
              },
            }
          : {
              amhpTeamId: selectedAmhpTeam?.id,
              // When the doctor invited an AMHP who wasn't already on the
              // admission, include their user ID so the server creates a work item.
              invitedAmhpUserId: invitedAmhpUser?.id,
            };

        const requestData: CreateSection12PaymentRequestInput = {
          formContextId: formContext.id,
          localAuthorityId: selectedLocalAuthority?.id,
          localAuthorityOdsCode: resolvedLa?.code,
          localAuthorityName:
            selectedLocalAuthority?.name ?? resolvedLa?.name ?? undefined,
          // The doctor-stated LA that arranged the assessment (guest invites).
          arrangingLocalAuthorityId: arrangingLocalAuthority?.id,
          arrangingLocalAuthorityName:
            arrangingLocalAuthority?.name?.trim() || undefined,
          outcomeType,
          items,
          // Include formId only for 'form' outcome type
          ...(outcomeType === "form" && selectedFormId
            ? { formId: selectedFormId }
            : {}),

          noMedRecReason:
            outcomeType === "no-med-rec"
              ? (capturedNoMedRecReason ?? recordedNoMedRecReason)
              : undefined,

          assessmentDateTime: assessmentDateTime
            ? assessmentDateTime.toISOString()
            : undefined,

          assessmentLocationAddress:
            assessmentLocationAddress.trim() || undefined,
          assessmentLocationPostcode:
            assessmentLocationPostcode.trim() || undefined,

          // Reaching submit requires ticking all declarations on step 3. The
          // server stamps the authoritative confirmation time.
          declarationsConfirmed: true,

          ...recipient,
        };

        await apiClient.createPaymentRequest(requestData);
        renderSuccessToast({
          message: "Payment request submitted successfully",
        });
      }
      reloadFormContext();
      onClose();
    } catch (error) {
      console.error("Failed to submit payment request:", error);
      setSubmitError(
        editingRequest
          ? "Failed to save payment request. Please try again."
          : "Failed to submit payment request. Please try again.",
      );
      renderErrorToast({
        message: editingRequest
          ? "Failed to save payment request"
          : "Failed to submit payment request",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={true}
        onClose={handleDialogClose}
        disableEscapeKeyDown={isBusy}
        fullWidth
        fullScreen={isMobile}
        maxWidth="sm"
        data-testid="s12-payment-request-dialog"
        slotProps={{
          paper: {
            sx: {
              display: "flex",
              flexDirection: "column",
              maxHeight: isMobile ? "100%" : "90vh",
            },
          },
        }}
      >
        {/* Fixed Header */}
        <DialogTitle
          sx={{
            py: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 600,
                }}
              >
                {isEditMode ? "Edit Payment Request" : "Payment Request"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Step {currentStep} of 3
              </Typography>
            </Box>
            <IconButton
              onClick={handleDialogClose}
              size={isMobile ? "medium" : "small"}
              edge="end"
              aria-label="close"
              data-testid="close-dialog-button"
              disabled={isBusy}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        {/* Scrollable Content */}
        <DialogContent
          sx={{
            flex: 1,
            overflowY: "auto",
            p: { xs: 2, sm: 3 },
            "&.MuiDialogContent-root": {
              pt: { xs: 2, sm: 3 },
            },
          }}
        >
          {/* Error/Warning Banners - only show on step 1 */}
          {currentStep === 1 &&
            (submitError ||
              isDuplicateRequest ||
              (existingDoctorRequests.length > 0 && !isDuplicateRequest)) && (
              <Box sx={{ mb: 2 }}>
                {submitError && (
                  <Banner
                    bannerType={BannerList.ERROR}
                    title="Error"
                    body={submitError}
                    sx={{ mb: 1.5 }}
                  />
                )}

                {duplicateOf !== undefined && (
                  <Banner
                    bannerType={BannerList.WARNING}
                    title="Duplicate Request"
                    body={match(duplicateOf)
                      .with(
                        "same-form",
                        () =>
                          "You have already submitted a payment request for this form.",
                      )
                      .with(
                        "replaced-form",
                        () =>
                          "You have already submitted a payment request for an earlier version of this form, before it was redone.",
                      )
                      .with(
                        "same-assessment",
                        () =>
                          "You have already submitted a payment request for this assessment.",
                      )
                      .exhaustive()}
                    sx={{ mb: 1.5 }}
                  />
                )}

                {existingDoctorRequests.length > 0 && !isDuplicateRequest && (
                  <Banner
                    bannerType={BannerList.INFO}
                    title="Existing Requests"
                    body={`You have ${String(existingDoctorRequests.length)} existing payment request${existingDoctorRequests.length > 1 ? "s" : ""} for this assessment.`}
                  />
                )}
              </Box>
            )}

          {/* Submit error on step 2 */}
          {(currentStep === 2 || currentStep === 3) && submitError && (
            <Box sx={{ mb: 2 }}>
              <Banner
                bannerType={BannerList.ERROR}
                title="Error"
                body={submitError}
              />
            </Box>
          )}

          {/* STEP 1: Assessment Details */}
          {currentStep === 1 && (
            <Box>
              {!isDuplicateRequest && existingDoctorRequests.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: 3,
                  }}
                >
                  Submit a payment request for your Section 12 assessment.
                </Typography>
              )}

              {/* AMHP Team Selection */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  AMHP Team
                </Typography>
                {(amhpTeams.length === 0 || isEditMode) && (
                  <Box>
                    {!isEditMode && (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", mb: 1.5 }}
                      >
                        No AMHP team is assigned to this work item. Search for
                        an AMHP to send this payment request to.
                      </Typography>
                    )}
                    {isEditMode && (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", mb: 1.5 }}
                      >
                        Search for a different AMHP to reassign this payment
                        request. The selected user&apos;s AMHP team becomes the
                        request&apos;s new team.
                      </Typography>
                    )}
                    <Typeahead<UserSearchResult>
                      enableFuse={false}
                      testId="amhp-invite-typeahead"
                      label="Search by name or email"
                      options={amhpSearchResults}
                      name="amhpInviteSearch"
                      inputValue={amhpSearchInputValue}
                      optionsKey="name"
                      // Allow a raw email so an unregistered AMHP can be invited
                      // as a guest (only when the org has the capability).
                      allowFreeText={canInviteGuestAmhp}
                      loading={amhpSearchLoading}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: "0.875rem",
                                backgroundColor: "primary.main",
                              }}
                            >
                              {getNameInitials(option.name)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">
                                {option.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                {option.email}
                                {option.contexts.filter(
                                  (c) => c.teamType === "amhp",
                                ).length === 1
                                  ? ` · ${option.contexts.find((c) => c.teamType === "amhp")!.name}`
                                  : ""}
                              </Typography>
                            </Box>
                          </Box>
                        </li>
                      )}
                      getOptionLabel={(option) =>
                        typeof option === "string"
                          ? option
                          : `${option.name} (${option.email})`
                      }
                      onInputChange={handleAmhpSearchInputChange}
                      onChange={handleAmhpUserSelected}
                    />
                    {/* If the invited AMHP has multiple AMHP teams, show a selector */}
                    {invitedAmhpUser && invitedAmhpTeamOptions.length > 1 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          This user belongs to multiple AMHP teams. Please
                          select one:
                        </Typography>
                        <AmhpTeamSelection
                          amhpTeams={invitedAmhpTeamOptions}
                          selectedAmhpTeam={selectedAmhpTeam}
                          setSelectedAmhpTeam={(team) => {
                            setSelectedAmhpTeam(withEditedRequestRouting(team));
                          }}
                          isMobile={isMobile}
                        />
                      </Box>
                    )}
                    {/* Guest AMHP invite — the typed email isn't a registered
                        account, so capture a name and show the guest warning. */}
                    {guestInviteMode && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Full Name
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          name="guestAmhpFullName"
                          slotProps={{
                            htmlInput: {
                              "data-testid": "guest-amhp-full-name",
                            },
                          }}
                          value={guestFullName}
                          onChange={(e) => {
                            setGuestFullName(e.target.value);
                          }}
                        />
                        <Banner
                          bannerType={BannerList.INFO}
                          body={t(
                            "components.newFormAssignee.guestUserClarification",
                          )}
                          sx={{ mt: 1.5 }}
                        />
                      </Box>
                    )}
                  </Box>
                )}
                {!isEditMode && onlyAmhpTeam !== undefined && (
                  <Typography variant="body2">{onlyAmhpTeam.name}</Typography>
                )}
                {!isEditMode && amhpTeams.length > 1 && (
                  <AmhpTeamSelection
                    amhpTeams={amhpTeams}
                    selectedAmhpTeam={selectedAmhpTeam}
                    setSelectedAmhpTeam={(team) => {
                      setSelectedAmhpTeam(withEditedRequestRouting(team));
                    }}
                    isMobile={isMobile}
                  />
                )}
                {hasAmhpTeam &&
                  !selectedAmhpTeamHasSection12PaymentsEnabled && (
                    <Banner
                      bannerType={BannerList.ERROR}
                      title="Dr Finder Payments Not Enabled"
                      body={AMHP_TEAM_SECTION12_PAYMENTS_DISABLED_MESSAGE}
                      sx={{ mt: 1.5 }}
                    />
                  )}
              </Box>

              {/* Local Authority Status */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {localAuthoritySelectionLabel}
                </Typography>
                {/* The patient's local authority is derived from their GP by
                    system rules and is never doctor-selectable — read-only in
                    every mode. */}
                {laLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Looking up local authority…
                  </Typography>
                ) : laError ? (
                  <Banner
                    bannerType={BannerList.WARNING}
                    body={
                      pricedByAnotherAuthority
                        ? "Unable to determine the local authority."
                        : "Unable to determine the local authority. Estimated fees will be used."
                    }
                    sx={{ mt: 1 }}
                  />
                ) : selectedLocalAuthority ? (
                  <>
                    <Typography variant="body2">
                      {selectedLocalAuthority.name}
                    </Typography>
                    {!selectedLocalAuthority.id && (
                      <Banner
                        bannerType={BannerList.INFO}
                        body="This local authority is not configured for precise payments. Estimated fees will be used."
                        sx={{ mt: 1 }}
                      />
                    )}
                  </>
                ) : (
                  <Banner
                    bannerType={BannerList.WARNING}
                    body={
                      isReconciliationEnabled
                        ? NO_LA_BANNER_RECONCILIATION_ON
                        : NO_LA_BANNER_RECONCILIATION_OFF
                    }
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              {/* Local authority which arranged this assessment — the doctor's
                  own input, captured on guest-AMHP invites. Distinct from the
                  system-derived responsible-for-payment LA above. */}
              {guestInviteMode && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {arrangingLocalAuthorityLabel}
                  </Typography>
                  <LocalAuthorityPicker
                    value={arrangingLocalAuthority}
                    onChange={setArrangingLocalAuthority}
                    ariaLabel={arrangingLocalAuthorityLabel}
                  />
                </Box>
              )}

              {/* Outcome Type Selection */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  What was the outcome of your assessment?
                </Typography>
                <OutcomeTypeSelection
                  outcomeType={outcomeType}
                  setOutcomeType={setOutcomeType}
                  setSelectedFormId={setSelectedFormId}
                  doctorSignedForms={doctorSignedForms}
                  showCalloutFeeOption={!!calloutFeeRate || !isPrecisePayments}
                  canRecordNoMedRec={canRecordNoMedRec}
                  noMedRecRefusal={noMedRec.refusal}
                  isMobile={isMobile}
                  isEditMode={isEditMode}
                />

                {/* Form Selection */}
                {outcomeType === "form" && (
                  <Box sx={{ mt: 2 }}>
                    <FormSelection
                      doctorSignedForms={doctorSignedForms}
                      selectedFormId={selectedFormId}
                      setSelectedFormId={setSelectedFormId}
                      priorRequestLinkByFormId={priorRequestLinkByFormId}
                      linkedFormReplacementId={linkedFormReplacement?.formId}
                      isMobile={isMobile}
                    />
                    {linkedFormReplacement !== undefined &&
                      selectedFormId === editingRequest?.formId && (
                        <Banner
                          bannerType={BannerList.INFO}
                          title="This form has been redone"
                          body="The form this request is linked to has been replaced by a new version. Select the new version to link this request to the current form."
                          sx={{ mt: 1.5 }}
                        />
                      )}
                  </Box>
                )}
              </Box>

              {/* Assessment Date/Time */}
              <Box sx={{ mt: 3 }} data-testid="field:assessmentDateTime">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  When did you attend the MHA assessment?
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  {isMobile ? (
                    <MobileDateTimePicker
                      ampm={false}
                      disableFuture
                      minDate={earliestAssessmentDate}
                      format="DD/MM/YYYY HH:mm"
                      value={assessmentDateTime}
                      onChange={handleAssessmentDateTimeChange}
                      onError={handleAssessmentDateTimeError}
                      slotProps={{
                        textField: {
                          error:
                            isAssessmentDateTimeInFuture ||
                            isAssessmentDateTimeTooFarInPast,
                          fullWidth: true,
                          size: "small",
                          helperText: isAssessmentDateTimeInFuture
                            ? ASSESSMENT_DATE_TIME_FUTURE_ERROR_MESSAGE
                            : isAssessmentDateTimeTooFarInPast
                              ? ASSESSMENT_DATE_TIME_PAST_ERROR_MESSAGE
                              : ASSESSMENT_DATE_TIME_HELPER_TEXT,
                        },
                      }}
                    />
                  ) : (
                    <DesktopDateTimePicker
                      ampm={false}
                      disableFuture
                      minDate={earliestAssessmentDate}
                      format="DD/MM/YYYY HH:mm"
                      value={assessmentDateTime}
                      onChange={handleAssessmentDateTimeChange}
                      onError={handleAssessmentDateTimeError}
                      slotProps={{
                        textField: {
                          error:
                            isAssessmentDateTimeInFuture ||
                            isAssessmentDateTimeTooFarInPast,
                          fullWidth: true,
                          size: "small",
                          helperText: isAssessmentDateTimeInFuture
                            ? ASSESSMENT_DATE_TIME_FUTURE_ERROR_MESSAGE
                            : isAssessmentDateTimeTooFarInPast
                              ? ASSESSMENT_DATE_TIME_PAST_ERROR_MESSAGE
                              : ASSESSMENT_DATE_TIME_HELPER_TEXT,
                        },
                      }}
                    />
                  )}
                </LocalizationProvider>
              </Box>

              {/* Assessment Location */}
              <Box sx={{ mt: 3 }} data-testid="field:assessmentLocation">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Where did the assessment take place?
                </Typography>
                <AddressField
                  name="assessmentLocation"
                  label=""
                  addressLabel="Address"
                  postalLabel="Postcode"
                  googleMapsApiKey={config.googleMapsApiKey}
                  addressCheckBoxLabel=""
                  hideConfirmation
                  value={{
                    address: assessmentLocationAddress,
                    postalCode: assessmentLocationPostcode,
                  }}
                  onChange={(values) => {
                    if (values) {
                      const v = values as Record<string, string>;
                      setAssessmentLocationAddress(v.address ?? "");
                      setAssessmentLocationPostcode(v.postalCode ?? "");
                    }
                  }}
                  testId="assessment-location"
                />
              </Box>
            </Box>
          )}

          {/* STEP 2: Invoice Details */}
          {currentStep === 2 && (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                Enter the details for your payment request.
              </Typography>

              <InvoiceItemsSection
                isPrecisePayments={isPrecisePayments}
                paymentRates={effectiveRates.paymentRates}
                selectedConfiguredRateKey={selectedConfiguredRateKey}
                setSelectedConfiguredRateKey={setSelectedConfiguredRateKey}
                outcomeType={outcomeType}
                mileageDistance={mileageDistance}
                setMileageDistance={setMileageDistance}
                mileageStartAddress={mileageStartAddress}
                setMileageStartAddress={setMileageStartAddress}
                otherAmountPounds={otherAmountPounds}
                setOtherAmountPounds={setOtherAmountPounds}
                otherDescription={otherDescription}
                setOtherDescription={setOtherDescription}
                expenseReceiptFiles={expenseReceiptFiles}
                expenseReceiptError={expenseReceiptError}
                otherReceiptRequiredError={otherReceiptRequiredError}
                hasOther={hasOther}
                otherItemExceedsLimit={otherItemExceedsLimit}
                isUploadingReceipts={isUploadingReceipts}
                onReceiptFilesSelected={handleReceiptFilesSelected}
                onRemoveReceipt={handleRemoveReceipt}
                totalAmountPence={totalAmountPence}
                formatPounds={formatPounds}
                isMobile={isMobile}
                effectiveRates={effectiveRates}
                laName={
                  pricedByAnotherAuthority
                    ? undefined
                    : selectedLaPaymentConfiguration?.laName
                }
                disableAmountEdits={lockAmountEdits}
              />
            </Box>
          )}

          {/* STEP 3: Declarations & confirmation */}
          {currentStep === 3 && (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                Please read and confirm the following before submitting your
                payment request.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="declaration-accept"
                    checked={declarationsAccepted}
                    onChange={(e) => setDeclarationsAccepted(e.target.checked)}
                    sx={{ color: "primary.main", pt: 0 }}
                  />
                }
                label={
                  <Stack spacing={1.5}>
                    <Typography variant="body2">
                      I confirm that I have read, understood and agree to the{" "}
                      <Link
                        href="https://www.thalamos.co.uk/terms-of-use/"
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms of Use
                      </Link>
                      .
                    </Typography>
                    <Typography variant="body2">
                      By claiming this fee, I certify that I am aware of, and
                      take any responsibility of, any National Insurance and Tax
                      liability associated with this payment. This service does
                      not form part of my contracted NHS duties.
                    </Typography>
                    <Typography variant="body2">
                      I confirm that the information provided is accurate and
                      complete. I understand that false or misleading
                      information may result in disciplinary, civil, or criminal
                      action. I consent to the use and sharing of this
                      information for claim verification and fraud prevention
                      purposes.
                    </Typography>
                  </Stack>
                }
                sx={{
                  alignItems: "flex-start",
                  mx: 0,
                  mb: 0.5,
                  p: 1.5,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: declarationsAccepted
                    ? "primary.main"
                    : "grey.300",
                  bgcolor: declarationsAccepted ? "primary.50" : "transparent",
                }}
              />
            </Box>
          )}
        </DialogContent>
        {/* Fixed Footer */}
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2 },
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "space-between",
          }}
        >
          {/* Left side: Cancel or Back */}
          {currentStep === 1 || (isLimitedEdit && currentStep === 2) ? (
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              disabled={isBusy}
              size={isMobile ? "large" : "medium"}
            >
              Cancel
            </Button>
          ) : (
            <Button
              onClick={() => {
                setCurrentStep(currentStep === 3 ? 2 : 1);
              }}
              variant="outlined"
              disabled={isBusy}
              size={isMobile ? "large" : "medium"}
              sx={{
                "& .MuiButton-startIcon": {
                  color: "inherit",
                },
              }}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          )}

          {/* Right side: Next or Submit */}
          {currentStep === 1 ? (
            <Button
              onClick={() => {
                // If "no-med-rec" is selected but the reason hasn't been
                // recorded yet (neither via the earlier journey nor inline),
                // divert the doctor to complete the reason dialog first.
                if (
                  outcomeType === "no-med-rec" &&
                  !recordedNoMedRecReason &&
                  !capturedNoMedRecReason
                ) {
                  setNoMedRecRefusal(undefined);
                  setShowNoMedRecDialog(true);
                  return;
                }
                setCurrentStep(2);
              }}
              variant="contained"
              color="primary"
              disabled={!canProceedToStep2}
              size={isMobile ? "large" : "medium"}
              sx={{
                "& .MuiButton-endIcon": {
                  color: "inherit",
                },
              }}
              endIcon={<ArrowForwardIcon />}
            >
              Next
            </Button>
          ) : currentStep === 2 ? (
            <Button
              onClick={() => {
                setCurrentStep(3);
              }}
              variant="contained"
              color="primary"
              disabled={!canSubmit}
              size={isMobile ? "large" : "medium"}
              sx={{
                "& .MuiButton-endIcon": {
                  color: "inherit",
                },
              }}
              endIcon={<ArrowForwardIcon />}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={() => {
                void handleSubmit();
              }}
              variant="contained"
              color="primary"
              disabled={!canSubmit || !allDeclarationsConfirmed}
              size={isMobile ? "large" : "medium"}
              sx={{
                "& .MuiButton-startIcon": {
                  color: "inherit",
                },
              }}
              startIcon={
                isBusy ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
            >
              {isSubmitting
                ? isEditMode
                  ? "Saving..."
                  : "Submitting..."
                : isUploadingReceipts
                  ? "Uploading receipts..."
                  : isEditMode
                    ? editingRequest?.status === "rejected"
                      ? "Resubmit for review"
                      : "Save changes"
                    : "Submit Request"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {showNoMedRecDialog && (
        <RecordReasonDialog
          title="I will not make a medical recommendation"
          dropDownLabel="Please enter a reason why you have chosen not to make a medical recommendation"
          options={NO_MED_REC_REASON_OPTIONS}
          loading={isRecordingReason}
          bannerTitle={noMedRecRefusal}
          onClose={() => {
            if (!isRecordingReason) {
              setShowNoMedRecDialog(false);
            }
          }}
          onConfirmClick={(reason, reasonDescription) => {
            const confirmNoMedRec = async () => {
              setNoMedRecRefusal(undefined);

              // Edit flow: only update the reason on the existing payment
              // request — don't retroactively change the work-item's recorded
              // not-proceeding action. The reason is sent with the payment
              // request update via `noMedRecReason`.
              if (isEditMode) {
                setCapturedNoMedRecReason(reason);
                setShowNoMedRecDialog(false);
                setCurrentStep(2);
                return;
              }

              if (!noMedRecWorkItem) {
                setNoMedRecRefusal(noMedRec.refusal);
                return;
              }

              setIsRecordingReason(true);
              try {
                const { status } =
                  await apiClient.recordReasonForNotProceedingMedRec(
                    noMedRecWorkItem.id,
                    reason,
                    reasonDescription,
                  );

                if (status === 204) {
                  setCapturedNoMedRecReason(reason);
                  setShowNoMedRecDialog(false);
                  setCurrentStep(2);
                  reloadFormContext();
                } else if (status === 404) {
                  // Refused, so what this session may do has changed under it —
                  // reload and let noMedRecWorkItem re-derive it.
                  logActionToDatadog("s12-no-med-rec-reason-refused");
                  reloadFormContext();
                  setNoMedRecRefusal(NO_MED_REC_WRONG_TEAM_MESSAGE);
                } else {
                  setNoMedRecRefusal("The reason could not be recorded.");
                }
              } catch {
                // No response, or the server failed outright — the one case
                // worth repeating.
                setNoMedRecRefusal(
                  "Failed to record reason. Please try again.",
                );
              } finally {
                setIsRecordingReason(false);
              }
            };
            void confirmNoMedRec();
          }}
        />
      )}
    </>
  );
}

/* ============================================
   Sub-components for cleaner organization
   ============================================ */

interface OutcomeTypeSelectionProps {
  outcomeType: Section12PaymentRequestOutcomeType;
  setOutcomeType: (type: Section12PaymentRequestOutcomeType) => void;
  setSelectedFormId: (id: string | null) => void;
  doctorSignedForms: SignedMedRecOption[];
  showCalloutFeeOption: boolean;
  canRecordNoMedRec: boolean;
  noMedRecRefusal?: string;
  isMobile: boolean;
  isEditMode: boolean;
}

function OutcomeTypeSelection({
  outcomeType,
  setOutcomeType,
  setSelectedFormId,
  doctorSignedForms,
  showCalloutFeeOption,
  canRecordNoMedRec,
  noMedRecRefusal,
  isMobile,
  isEditMode,
}: OutcomeTypeSelectionProps) {
  return (
    <RadioGroup
      value={outcomeType}
      onChange={(e) => {
        setOutcomeType(e.target.value as Section12PaymentRequestOutcomeType);
        if (e.target.value !== "form") {
          setSelectedFormId(null);
        }
      }}
    >
      <FormControlLabel
        value="form"
        control={<Radio size={isMobile ? "medium" : "small"} />}
        label={
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
              }}
            >
              Completed a medical recommendation
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Signed a form on Thalamos
            </Typography>
          </Box>
        }
        disabled={
          doctorSignedForms.length === 0 &&
          !(isEditMode && outcomeType === "form")
        }
        sx={{
          mb: 0.5,
          p: { xs: 1.5, sm: 1 },
          mx: 0,
          borderRadius: 1,
          bgcolor: outcomeType === "form" ? "action.selected" : "transparent",
          "&:hover": { bgcolor: "action.hover" },
        }}
      />
      <FormControlLabel
        value="no-med-rec"
        control={<Radio size={isMobile ? "medium" : "small"} />}
        label={
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
              }}
            >
              No medical recommendation made
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
              }}
            >
              Assessment completed, no recommendation needed
            </Typography>
            {!canRecordNoMedRec && noMedRecRefusal && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                }}
              >
                {noMedRecRefusal}
              </Typography>
            )}
          </Box>
        }
        disabled={!canRecordNoMedRec}
        sx={{
          mb: 0.5,
          p: { xs: 1.5, sm: 1 },
          mx: 0,
          borderRadius: 1,
          bgcolor:
            outcomeType === "no-med-rec" ? "action.selected" : "transparent",
          "&:hover": { bgcolor: "action.hover" },
        }}
      />
      <FormControlLabel
        value="completed-outside-thalamos"
        control={<Radio size={isMobile ? "medium" : "small"} />}
        label={
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
              }}
            >
              Completed outside Thalamos
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Paper form or other system
            </Typography>
          </Box>
        }
        sx={{
          mb: showCalloutFeeOption ? 0.5 : 0,
          p: { xs: 1.5, sm: 1 },
          mx: 0,
          borderRadius: 1,
          bgcolor:
            outcomeType === "completed-outside-thalamos"
              ? "action.selected"
              : "transparent",
          "&:hover": { bgcolor: "action.hover" },
        }}
      />
      {showCalloutFeeOption && (
        <FormControlLabel
          value="call-out-fee"
          control={<Radio size={isMobile ? "medium" : "small"} />}
          label={
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                }}
              >
                No assessment done (callout fee)
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Attended but no assessment was carried out
              </Typography>
            </Box>
          }
          sx={{
            p: { xs: 1.5, sm: 1 },
            mx: 0,
            borderRadius: 1,
            bgcolor:
              outcomeType === "call-out-fee"
                ? "action.selected"
                : "transparent",
            "&:hover": { bgcolor: "action.hover" },
          }}
        />
      )}
    </RadioGroup>
  );
}

interface FormSelectionProps {
  doctorSignedForms: SignedMedRecOption[];
  selectedFormId: string | null;
  setSelectedFormId: (id: string | null) => void;
  priorRequestLinkByFormId: ReadonlyMap<string, PriorRequestLink>;
  /** The redo replacement of the voided form the edited request is linked to. */
  linkedFormReplacementId: string | undefined;
  isMobile: boolean;
}

function FormSelection({
  doctorSignedForms,
  selectedFormId,
  setSelectedFormId,
  priorRequestLinkByFormId,
  linkedFormReplacementId,
  isMobile,
}: FormSelectionProps) {
  if (doctorSignedForms.length === 0) {
    return (
      <Banner
        bannerType={BannerList.ERROR}
        title="No Signed Forms"
        body="You have not signed any medical recommendation forms on this assessment."
      />
    );
  }

  const form =
    doctorSignedForms.length === 1 ? doctorSignedForms[0] : undefined;
  if (form !== undefined) {
    const prior = priorRequestLinkByFormId.get(form.formId);
    return (
      <Box
        sx={{
          p: 2,
          bgcolor: "grey.50",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
          }}
        >
          {form.formName}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
          }}
        >
          Signed:{" "}
          {new Date(form.signatureDate).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </Typography>
        {prior !== undefined && (
          <Chip
            label={match(prior)
              .with("same-form", () => "Already submitted")
              .with("replaced-form", () => "Earlier version submitted")
              .exhaustive()}
            size="small"
            color="warning"
            sx={{ ml: 1, verticalAlign: "middle" }}
          />
        )}
      </Box>
    );
  }

  return (
    <RadioGroup
      value={selectedFormId ?? ""}
      onChange={(e) => {
        setSelectedFormId(e.target.value || null);
      }}
    >
      {doctorSignedForms.map((form) => {
        const prior = priorRequestLinkByFormId.get(form.formId);
        return (
          <FormControlLabel
            key={form.formId}
            value={form.formId}
            control={<Radio size={isMobile ? "medium" : "small"} />}
            label={
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {form.formName}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    Signed:{" "}
                    {new Date(form.signatureDate).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </Typography>
                  {form.isVoid && (
                    <Chip
                      label={redoStatusLabel({ redoState: "void" })}
                      size="small"
                    />
                  )}
                  {form.formId === linkedFormReplacementId && (
                    <Chip label="New version" size="small" color="info" />
                  )}
                  {prior !== undefined && (
                    <Chip
                      label={match(prior)
                        .with("same-form", () => "Submitted")
                        .with(
                          "replaced-form",
                          () => "Earlier version submitted",
                        )
                        .exhaustive()}
                      size="small"
                      color="warning"
                    />
                  )}
                </Box>
              </Box>
            }
            sx={{
              mb: 1,
              p: { xs: 1.5, sm: 1 },
              mx: 0,
              borderRadius: 1,
              bgcolor:
                selectedFormId === form.formId
                  ? "action.selected"
                  : "transparent",
              "&:hover": { bgcolor: "action.hover" },
            }}
          />
        );
      })}
    </RadioGroup>
  );
}

interface AmhpTeamSelectionProps {
  amhpTeams: AmhpTeamOption[];
  selectedAmhpTeam: AmhpTeamOption | null;
  setSelectedAmhpTeam: (team: AmhpTeamOption) => void;
  isMobile: boolean;
}

function AmhpTeamSelection({
  amhpTeams,
  selectedAmhpTeam,
  setSelectedAmhpTeam,
  isMobile,
}: AmhpTeamSelectionProps) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {amhpTeams.map((team) => (
        <Chip
          key={team.id}
          label={team.name}
          onClick={() => {
            setSelectedAmhpTeam(team);
          }}
          color={selectedAmhpTeam?.id === team.id ? "primary" : "default"}
          variant={selectedAmhpTeam?.id === team.id ? "filled" : "outlined"}
          size={isMobile ? "medium" : "small"}
          sx={{
            height: isMobile ? 40 : 32,
            fontSize: isMobile ? "0.875rem" : "0.8125rem",
          }}
        />
      ))}
    </Box>
  );
}

interface LocalAuthoritySelectionProps {
  label: string;
  userLocalAuthorityMappings: {
    id: string;
    locationId: string;
    locationName: string;
  }[];
  selectedLocalAuthority: SelectedLocalAuthority;
  showOtherPicker: boolean;
  handleChipClick: (locationId: string, locationName: string) => void;
  handleOtherClick: () => void;
  setSelectedLocalAuthority: (value: SelectedLocalAuthority) => void;
  hasLocalAuthority: boolean;
  isMobile: boolean;
}

// eslint-disable-next-line no-unused-vars -- retained for future use
function LocalAuthoritySelection({
  label,
  userLocalAuthorityMappings,
  selectedLocalAuthority,
  showOtherPicker,
  handleChipClick,
  handleOtherClick,
  setSelectedLocalAuthority,
  hasLocalAuthority,
  isMobile,
}: LocalAuthoritySelectionProps) {
  return (
    <>
      {userLocalAuthorityMappings.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: showOtherPicker ? 2 : 0,
          }}
        >
          {userLocalAuthorityMappings.map((mapping) => (
            <Chip
              key={mapping.id}
              label={mapping.locationName}
              onClick={() => {
                handleChipClick(mapping.locationId, mapping.locationName);
              }}
              color={
                selectedLocalAuthority?.id === mapping.locationId
                  ? "primary"
                  : "default"
              }
              variant={
                selectedLocalAuthority?.id === mapping.locationId
                  ? "filled"
                  : "outlined"
              }
              size={isMobile ? "medium" : "small"}
              sx={{
                height: isMobile ? 40 : 32,
                fontSize: isMobile ? "0.875rem" : "0.8125rem",
              }}
            />
          ))}
          <Chip
            label="Other"
            onClick={handleOtherClick}
            color={showOtherPicker ? "primary" : "default"}
            variant={showOtherPicker ? "filled" : "outlined"}
            size={isMobile ? "medium" : "small"}
            sx={{
              height: isMobile ? 40 : 32,
              fontSize: isMobile ? "0.875rem" : "0.8125rem",
            }}
          />
        </Box>
      )}

      {(showOtherPicker || userLocalAuthorityMappings.length === 0) && (
        <LocalAuthorityPicker
          value={selectedLocalAuthority}
          onChange={setSelectedLocalAuthority}
          label=""
          ariaLabel={label}
          error={
            !hasLocalAuthority && showOtherPicker
              ? "Please select a local authority"
              : undefined
          }
        />
      )}
    </>
  );
}

interface EffectiveRates {
  paymentRates: LocalAuthorityPaymentRate[];
  mileageRatePencePerMile: number;
  otherItemLimitPence: number | null;
}

interface InvoiceItemsSectionProps {
  isPrecisePayments: boolean;
  paymentRates: LocalAuthorityPaymentRate[];
  selectedConfiguredRateKey: string;
  setSelectedConfiguredRateKey: (key: string) => void;
  outcomeType: Section12PaymentRequestOutcomeType;
  mileageDistance: string;
  setMileageDistance: (value: string) => void;
  mileageStartAddress: { address: string; postalCode: string };
  setMileageStartAddress: (value: {
    address: string;
    postalCode: string;
  }) => void;
  otherAmountPounds: string;
  setOtherAmountPounds: (value: string) => void;
  otherDescription: string;
  setOtherDescription: (value: string) => void;
  expenseReceiptFiles: UploadedFile[];
  expenseReceiptError: string | null;
  otherReceiptRequiredError: string | null;
  hasOther: boolean;
  otherItemExceedsLimit: boolean;
  isUploadingReceipts: boolean;
  onReceiptFilesSelected: (files: UploadedFile[]) => Promise<void>;
  onRemoveReceipt: (index: number) => void;
  totalAmountPence: number;
  formatPounds: (pence: number) => string;
  isMobile: boolean;
  effectiveRates: EffectiveRates;
  laName?: string;
  // receipt-missing limited edit: rates/amounts are locked, receipts only.
  disableAmountEdits?: boolean;
}

function InvoiceItemsSection({
  isPrecisePayments,
  paymentRates,
  selectedConfiguredRateKey,
  setSelectedConfiguredRateKey,
  outcomeType,
  mileageDistance,
  setMileageDistance,
  mileageStartAddress,
  setMileageStartAddress,
  otherAmountPounds,
  setOtherAmountPounds,
  otherDescription,
  setOtherDescription,
  expenseReceiptFiles,
  expenseReceiptError,
  otherReceiptRequiredError,
  hasOther,
  otherItemExceedsLimit,
  isUploadingReceipts,
  onReceiptFilesSelected,
  onRemoveReceipt,
  totalAmountPence,
  formatPounds,
  isMobile,
  effectiveRates,
  laName,
  disableAmountEdits = false,
}: InvoiceItemsSectionProps) {
  // Calculate the derived mileage amount for display
  const derivedMileagePounds = (() => {
    const miles = parseFloat(mileageDistance || "0");
    if (isNaN(miles) || miles <= 0) return null;
    const pence = Math.round(miles * effectiveRates.mileageRatePencePerMile);
    return (pence / 100).toFixed(2);
  })();

  const hasMileage = mileageDistance !== "" && parseFloat(mileageDistance) > 0;

  return (
    <Box>
      {/* Info banner when using LA-configured rates */}
      {isPrecisePayments && laName && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Using payment rates configured for {laName}
        </Alert>
      )}

      {/* Configured Fee Selection */}
      {isPrecisePayments ? (
        <>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: "text.secondary",
              mb: 1,
            }}
          >
            Configured Fee
          </Typography>
          <RadioGroup
            value={selectedConfiguredRateKey}
            onChange={(e) => {
              setSelectedConfiguredRateKey(e.target.value);
            }}
            sx={{ mb: 2 }}
          >
            {paymentRates.map((rate, index) => {
              const isSelected = selectedConfiguredRateKey === rate.key;
              // When callout-fee outcome is selected, only the call-out-fee rate is
              // selectable; all other configured fees (day rate, night rate, etc.)
              // are disabled.
              const isDisabled =
                outcomeType === "call-out-fee" && rate.key !== "call-out-fee";
              const radioDisabled = isDisabled || disableAmountEdits;

              return (
                <FormControlLabel
                  key={rate.key}
                  value={rate.key}
                  disabled={radioDisabled}
                  control={<Radio size={isMobile ? "medium" : "small"} />}
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2">{rate.label}</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        £{(rate.amountPence / 100).toFixed(2)}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    p: 1,
                    mx: 0,
                    mb: index === paymentRates.length - 1 ? 0 : 0.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: isSelected ? "primary.main" : "grey.300",
                    bgcolor: isSelected ? "primary.50" : "transparent",
                    opacity: radioDisabled ? 0.4 : 1,
                    pointerEvents: radioDisabled ? "none" : "auto",
                    "& .MuiFormControlLabel-label": { flex: 1 },
                  }}
                />
              );
            })}
          </RadioGroup>
        </>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Estimated Payment Fee
          </Typography>
          <Typography variant="body2">
            Completed assessment: £170 - £190.
          </Typography>
          <Typography variant="body2">
            Callout only: to be confirmed following approval.
          </Typography>
        </Alert>
      )}
      {/* Mileage Section */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: "text.secondary",
          mb: 1,
        }}
      >
        Mileage (optional)
      </Typography>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Distance (miles)"
          type="number"
          value={mileageDistance}
          disabled={disableAmountEdits}
          onChange={(e) => {
            setMileageDistance(e.target.value);
          }}
          sx={{ mb: 1 }}
          data-testid="mileage-distance-input"
          helperText={
            isPrecisePayments
              ? derivedMileagePounds !== null
                ? `${mileageDistance} miles × ${String(effectiveRates.mileageRatePencePerMile)}p = £${derivedMileagePounds}`
                : `Rate: ${String(effectiveRates.mileageRatePencePerMile)}p per mile`
              : undefined
          }
          slotProps={{
            htmlInput: {
              min: 0,
              step: 0.01,
              onWheel: blurNumberInputOnWheel,
            },
          }}
        />
        {isPrecisePayments && derivedMileagePounds !== null && (
          <TextField
            fullWidth
            size="small"
            label="Mileage amount"
            value={`£${derivedMileagePounds}`}
            data-testid="mileage-amount-display"
            sx={{ mb: 1 }}
            slotProps={{
              input: { readOnly: true },
              htmlInput: { "aria-label": "Derived mileage amount (read-only)" },
            }}
          />
        )}
        {hasMileage && (
          <AddressField
            name="mileageStartLocation"
            label="Start location"
            addressLabel="Address"
            postalLabel="Postcode"
            googleMapsApiKey={config.googleMapsApiKey}
            addressCheckBoxLabel=""
            hideConfirmation
            readOnly={disableAmountEdits}
            value={{
              address: mileageStartAddress.address,
              postalCode: mileageStartAddress.postalCode,
            }}
            onChange={(values: { address: string; postalCode: string }) => {
              setMileageStartAddress({
                address: values.address ?? "",
                postalCode: values.postalCode ?? "",
              });
            }}
            errors={{
              address:
                hasMileage && mileageStartAddress.address.trim() === ""
                  ? "Please enter your start location"
                  : undefined,
            }}
            testId="mileage-start-location"
          />
        )}
      </Box>
      {/* Other Section */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: "text.secondary",
          mb: 1,
        }}
      >
        Other (optional)
      </Typography>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Amount"
          type="number"
          value={otherAmountPounds}
          disabled={disableAmountEdits}
          onChange={(e) => {
            setOtherAmountPounds(e.target.value);
          }}
          sx={{ mb: 1 }}
          data-testid="other-amount-input"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">£</InputAdornment>
              ),
            },

            htmlInput: {
              min: 0,
              step: 0.01,
              onWheel: blurNumberInputOnWheel,
            },
          }}
          helperText={
            otherItemExceedsLimit && effectiveRates.otherItemLimitPence !== null
              ? `This local authority limits Other items to ${formatPounds(effectiveRates.otherItemLimitPence)}`
              : effectiveRates.otherItemLimitPence !== null
                ? `Maximum allowed: ${formatPounds(effectiveRates.otherItemLimitPence)}`
                : undefined
          }
          error={otherItemExceedsLimit}
        />
        {hasOther && (
          <>
            <TextField
              fullWidth
              size="small"
              label="Description (e.g., parking, tolls)"
              value={otherDescription}
              disabled={disableAmountEdits}
              onChange={(e) => {
                setOtherDescription(e.target.value);
              }}
              slotProps={{
                htmlInput: { maxLength: 200 },
              }}
              sx={{ mb: 1 }}
            />

            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ mb: 1, mt: 1.5 }}
              color="text.secondary"
            >
              Receipts (required)
            </Typography>

            <Base64FileUpload
              onDone={(files) => {
                void onReceiptFilesSelected(files);
              }}
              label={
                isUploadingReceipts
                  ? "Uploading receipts..."
                  : "Add receipt files"
              }
              accept={RECEIPT_ACCEPT}
              multi={true}
              disabled={isUploadingReceipts}
              uploadSource="cameraOrFiles"
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Upload at least one receipt. PDF, JPG, PNG or WEBP up to 5MB each.
            </Typography>

            {otherReceiptRequiredError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {otherReceiptRequiredError}
              </Alert>
            )}

            {expenseReceiptError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {expenseReceiptError}
              </Alert>
            )}

            {expenseReceiptFiles.length > 0 && (
              <Box sx={{ mt: 1.5, display: "grid", gap: 1 }}>
                {expenseReceiptFiles.map((file, index) => {
                  const isPdf = file.type === "application/pdf";
                  const previewSource =
                    typeof file.base64 === "string" ? file.base64 : "";

                  return (
                    <Box
                      key={`receipt-${String(index)}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      {isPdf ? (
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            flexShrink: 0,
                          }}
                        >
                          <PictureAsPdfIcon color="action" />
                        </Box>
                      ) : (
                        <Box
                          component="img"
                          src={previewSource}
                          alt={file.name}
                          sx={{
                            width: 56,
                            height: 56,
                            objectFit: "cover",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            flexShrink: 0,
                          }}
                        />
                      )}

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {file.uploadedKey ? "Uploaded" : "Ready"}
                        </Typography>
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() => {
                          onRemoveReceipt(index);
                        }}
                        disabled={isUploadingReceipts}
                        aria-label={`Remove ${file.name}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Box>
      {/* Total Summary */}
      {isPrecisePayments && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1.5,
              bgcolor: "primary.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "primary.200",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
              }}
            >
              Total
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "primary.main",
              }}
            >
              {formatPounds(totalAmountPence)}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
